import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Copy,
  Info,
  CheckCircle,
  XCircle,
} from "lucide-react";

const ContainsDuplicateVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    { id: 1, name: "Has Duplicates", array: [1, 2, 3, 1] },
    { id: 2, name: "No Duplicates", array: [1, 2, 3, 4] },
    {
      id: 3,
      name: "Multiple Duplicates",
      array: [1, 1, 1, 3, 3, 4, 3, 2, 4, 2],
    },
    { id: 4, name: "Empty Array", array: [] },
    { id: 5, name: "Single Element", array: [5] },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [array, setArray] = useState(defaultTestCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function containsDuplicate(nums):", indent: 0 },
    { line: "seen = new Set()", indent: 1 },
    { line: "for num in nums:", indent: 1 },
    { line: "if seen.has(num):", indent: 2 },
    { line: "return true", indent: 3 },
    { line: "seen.add(num)", indent: 2 },
    { line: "return false", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (nums) => {
    const allSteps = [];
    let stepCount = 0;
    const seen = new Set();
    let result = null;

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        seen: Array.from(seen),
        result,
        ...details,
      });
    };

    addStep("Start: Initialize an empty set.", 1);

    for (let i = 0; i < nums.length; i++) {
      const num = nums[i];
      addStep(`Processing index ${i}, value ${num}.`, 2, { currentIndex: i });

      addStep(`Check if ${num} is in the set.`, 3, { currentIndex: i });
      if (seen.has(num)) {
        result = true;
        addStep(`Duplicate ${num} found in the set. Return true.`, 4, {
          currentIndex: i,
          duplicateValue: num,
          isFinal: true,
          result,
        });
        return allSteps;
      }

      seen.add(num);
      addStep(`${num} is not in the set. Add it.`, 5, { currentIndex: i });
    }

    result = false;
    addStep("Finished loop. No duplicates found. Return false.", 6, {
      isFinal: true,
      result,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(array);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [array]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setArray(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      if (!Array.isArray(nums) || nums.some(isNaN)) {
        throw new Error("Input must be an array of numbers.");
      }
      const newTestCase = { id: Date.now(), name: `Custom`, array: nums };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format: ${error.message}. Use comma-separated numbers.`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Copy className="text-blue-500" /> Contains Duplicate
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an integer array `nums`, return `true` if any value
                appears at least twice in the array, and `false` if every
                element is distinct.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Array: 1,2,3,1"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4">
              <p className="text-sm font-semibold mb-2">Input Array:</p>
              <div className="flex flex-wrap gap-2">
                {array.map((num, idx) => (
                  <div key={idx} className="text-center">
                    <div
                      className={`w-14 h-14 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-all duration-300
                      ${
                        currentStepData.duplicateValue === num &&
                        idx === currentStepData.currentIndex
                          ? "bg-red-200 border-red-400 scale-110"
                          : idx === currentStepData.currentIndex
                          ? "bg-blue-200 border-blue-400 scale-110"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      {num}
                    </div>
                    <span className="text-xs text-gray-500 mt-1">{idx}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700">
                Set (Seen Elements)
              </h4>
              <div className="p-2 bg-gray-50 rounded mt-1 flex flex-wrap gap-2 justify-center min-h-[40px]">
                {currentStepData.seen?.map((val) => (
                  <span
                    key={val}
                    className="font-mono text-sm p-2 bg-gray-200 rounded"
                  >
                    {val}
                  </span>
                ))}
              </div>
            </div>

            {currentStepData.isFinal && (
              <div
                className={`mt-4 p-3 rounded-lg text-center ${
                  currentStepData.result ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <div
                  className={`flex items-center justify-center font-bold ${
                    currentStepData.result ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {currentStepData.result ? (
                    <CheckCircle className="mr-2" />
                  ) : (
                    <XCircle className="mr-2" />
                  )}
                  Result:{" "}
                  {currentStepData.result
                    ? "Contains Duplicate"
                    : "No Duplicates"}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              HashSet Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The optimal solution uses a HashSet (or a Set in JavaScript) to
              achieve linear time complexity. We iterate through the array once.
              For each element, we check if it already exists in the set. If it
              does, we've found a duplicate and can return `true` immediately.
              If not, we add the element to the set and continue. If we finish
              the loop without finding duplicates, we return `false`.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(N)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Using a Set provides average O(1) time complexity for
                  insertions and lookups, which is key to the overall O(N)
                  performance.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  This approach is a trade-off, using extra memory (O(N) space)
                  to gain a significant speed advantage over a brute-force O(N²)
                  solution.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The algorithm can stop and return `true` as soon as the first
                  duplicate is found.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContainsDuplicateVisualizer;
